import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { UserStats } from '../../types/user';
import { parseAdminDashboardTab } from '../../utils/adminDashboardTabs';
import AdminLayout from './AdminLayout';
import AdminOverview from './AdminOverview';
import UserManagement from './UserManagement';
import SubscriptionManagement from './SubscriptionManagement';
import PaymentManagement from './PaymentManagement';
import PricingManagement from './PricingManagement';
import CrisisCommunicationsAdmin from './CrisisCommunicationsAdmin';
import TabletopManagement from './TabletopManagement';
import TrainingManagement from './TrainingManagement';
import SecuritySettings from './SecuritySettings';
import BCPManagement from './BCPManagement';
import InsurerManagement from './InsurerManagement';
import TemplateManagement from './TemplateManagement';
import DemoRequestManagement from './DemoRequestManagement';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [partnerMetrics, setPartnerMetrics] = useState<{
    pending_insurer_orgs: number;
    active_insurer_orgs: number;
    outstanding_ledger_cents: number;
    credits_granted_mtd: number;
    audit_requests_mtd: number;
    simple_tokens_mtd: number;
    gold_tokens_mtd: number;
    premium_tokens_mtd: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const activeTab = parseAdminDashboardTab(searchParams);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const statsData = await adminService.getUserStats();
      setStats(statsData);
      try {
        const partnerData = await adminService.getPartnerMetrics();
        setPartnerMetrics(partnerData);
      } catch {
        setPartnerMetrics(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load admin dashboard');
    } finally {
      setLoading(false);
    }
  };

  const inactiveCount = stats
    ? Math.max(0, stats.total_users - stats.active_users)
    : 0;

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-slate-500 font-medium">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md bg-white p-8 rounded-xl shadow-sm border border-slate-200">
          <div className="text-rose-600 text-3xl mb-3">⚠️</div>
          <h2 className="text-lg font-bold text-slate-900 mb-2">Error Loading Admin</h2>
          <p className="text-sm text-slate-600 mb-5">{error}</p>
          <button
            onClick={loadStats}
            className="px-4 py-2 bg-[#991B1B] hover:bg-[#7F1D1D] text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout inactiveUserCount={inactiveCount}>
      {activeTab === 'overview' && stats && (
        <AdminOverview
          stats={stats}
          partnerMetrics={partnerMetrics}
          onRefresh={loadStats}
        />
      )}

      {activeTab === 'users' && <UserManagement />}

      {activeTab === 'subscriptions' && <SubscriptionManagement />}

      {activeTab === 'payments' && <PaymentManagement />}

      {activeTab === 'pricing' && <PricingManagement />}

      {activeTab === 'demo-requests' && <DemoRequestManagement />}

      {activeTab === 'crisis-communications' && <CrisisCommunicationsAdmin />}

      {activeTab === 'training' && <TrainingManagement />}

      {activeTab === 'tabletop' && <TabletopManagement />}

      {activeTab === 'templates' && <TemplateManagement />}

      {activeTab === 'bcps' && <BCPManagement />}

      {activeTab === 'insurers' && <InsurerManagement />}

      {activeTab === 'security' && <SecuritySettings />}
    </AdminLayout>
  );
};

export default AdminDashboard;
